import { Map } from 'immutable'
import _parseInt from 'lodash/parseInt'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import { all as allPromise } from 'bluebird'
import { SET_PAGE_READY } from 'services/pages/redux/actions'
import { SET_LOCATION_DATA } from 'services/location/redux/actions'
import {
  GET_REDIRECTS,
  REMOVE_REDIRECT,
  CREATE_REDIRECT,
  UPDATE_REDIRECT,
  PROCESS_BULK_UPLOAD,
  setRedirects as setRedirectsAction,
  getRedirects as getRedirectsAction,
  setRedirectsOffsetLimit as setRedirectsOffsetLimitAction,
  setError,
} from './actions'
import {
  getRedirectsList,
  deleteRedirect,
  upsertRedirect,
} from '..'

const log = console

/**
 * Format errors for the display alert
 */
function formatAlert (error, message) {
  const errBody = _get(error, ['response', 'body', 'errors', 0])
  return `${message}\n     status= ${errBody.status}\n     details: ${errBody.title}, ${errBody.detail}, ${errBody.meta}`
}

/**
 * Handle the page ready event at this point we have auth and config
 */
async function pageReadyHandler ({ store, action }) {
  const { payload } = action
  if (payload === true) {
    store.dispatch(getRedirectsAction({ storeKey: 'redirectsList' }))
  }
}

/**
 * Handle getting Redirects
 */
async function getRedirectsHandler ({ store, action }) {
  const {
    config, auth, redirects, location,
  } = store.getState()
  const {
    storeKey,
    offset = _parseInt(location.getIn(['data', 'query', 'offset'], 0)),
    limit = _parseInt(location.getIn(['data', 'query', 'limit'], 100)),
    source = location.getIn('data', 'query', 'source'),
    redirect = location.get('data', 'query', 'redirect'),
  } = action.payload
  if (!storeKey) {
    throw new Error('StoreKey required to get redirects')
  }
  const processing = redirects.getIn([storeKey, 'processing'])
  const query = location.getIn(['data', 'query'])
  const hasChanged = (redirects.getIn([storeKey, 'offset']) !== offset)
    || (redirects.getIn([storeKey, 'source']) !== source)
    || (redirects.getIn([storeKey, 'redirect']) !== redirect)
  if (processing || !hasChanged) {
    return
  }
  try {
    store.dispatch(setRedirectsOffsetLimitAction({ storeKey, offset, limit }))
    const redirectsList = await getRedirectsList({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      uid: query.get('uid'),
      source: query.get('source'),
      redirect: query.get('redirect'),
      type: query.get('statusCode'),
      offset,
      limit,
    })
    const redirectData = redirectsList.get('pathRedirects')
    store.dispatch(setRedirectsAction({
      storeKey,
      data: redirectData,
      total: redirectsList.get('total'),
    }))
  } catch (error) {
    log.error(error, 'Error fetching redirect list')
    store.dispatch(setError(formatAlert(error, 'Error fetching redirect list')))
  }
}

/**
 * Handle the remove request.  Note, we also need to refetch the list based to refresh the page
 */
async function removeRedirectHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { payload = {} } = action
  const { id } = payload
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  try {
    await deleteRedirect({ server, id, auth })
    await getRedirectsHandler({ store, action })
  } catch (error) {
    log.error(error, 'Error removing redirect.')
    store.dispatch(setError(formatAlert(error, 'Error removing redirect')))
  }
}

/**
 * Handle the create/update requests. Request without and id is a create
 *   Note, we also need to refetch the list based to refresh the page
 */
async function upsertRedirectHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { payload = {} } = action
  const { data } = payload
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  try {
    await upsertRedirect({ server, data, auth })
    await getRedirectsHandler({ store, action })
  } catch (error) {
    log.error(error, 'Error upserting redirect.')
    store.dispatch(setError(formatAlert(error, 'Could not upsert redirect')))
  }
}

/**
 * Handle the create for a bulk upload.
 *   Note, we also need to refetch the list based to refresh the page
 */
async function processBulkUpload ({ store, action }) {
  const { config, auth } = store.getState()
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  const { payload = {} } = action
  const { data } = payload
  try {
    const promises = data.map(async (redirect) => {
      await upsertRedirect({ server, data: redirect, auth })
    })
    await allPromise(promises)
    await getRedirectsHandler({ store, action })
  } catch (error) {
    log.error(error, 'Error performing bulk redirect.')
    store.dispatch(setError(formatAlert(error, 'Could not upload bulk redirects')))
  }
}

function isRedirectsPage (location) {
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map')
  }
  return _includes(
    ['/content/redirects', '/url-redirects'],
    location.getIn(['data', 'pathname']),
  )
}

export default (store) => (next) => (action) => {
  const { type } = action
  switch (type) {
    case SET_PAGE_READY: {
      const { location = Map() } = store.getState()
      if (isRedirectsPage(location)) { pageReadyHandler({ store, action }) }
      break
    }
    case GET_REDIRECTS:
      getRedirectsHandler({ store, action })
      break
    case REMOVE_REDIRECT:
      removeRedirectHandler({ store, action })
      break
    case CREATE_REDIRECT:
    case UPDATE_REDIRECT:
      upsertRedirectHandler({ store, action })
      break
    case PROCESS_BULK_UPLOAD:
      processBulkUpload({ store, action })
      break
    default:
      break
  }
  next(action)
  switch (type) {
    case SET_LOCATION_DATA: {
      const { location = Map(), page = Map() } = store.getState()
      if (isRedirectsPage(location) && page.get('ready')) {
        store.dispatch(getRedirectsAction({ storeKey: 'redirectsList' }))
      }
      break
    }
    default:
      break
  }
}
